import Button from 'components/Button'
import Icon from 'components/Icon'
import SelectField from 'features/forms/components/SelectField'
import TextField from 'features/forms/components/TextField'
import { OrganizationFragment, OrganizationLanguage } from 'generated/graphql'
import React from 'react'
import { Col, Row } from 'react-bootstrap'
import styled from 'styled-components'
import { format } from 'date-fns'
import { toDate } from 'utils/datetime'
import { colors, _ } from 'utils'
import { $gray_600 } from 'utils/colors'
import OverviewItem from './OverviewItem'

export const S = {
    Container: styled.div`
        padding: 0 10px;
    `,
    SectionHeader: styled.h2`
        font-weight: bold;
        font-size: 20px;
        line-height: 23px;
    `,
    FormActions: styled.div`
        width: 100%;
        display: flex;
        margin: 0 auto;
        padding: 15px 20px;
        justify-content: center;
    `,
    Overview: {
        Container: styled.div`
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            padding: 20px;
            gap: 20px;
            margin: -41px -20px 20px -20px;
            border-top: 1px solid ${colors.statusBorder};
            border-bottom: 1px solid ${colors.statusBorder};
        `,
        Item: styled.div`
            padding: 16px 20px;
            position: relative;
            border: 1px solid ${colors.iconOpacity(0.25)};
            background-color: ${colors.primaryOpacity(0.02)};
            display: flex;
            align-items: center;
            border-radius: 2px;
        `,
        Icon: styled(Icon).attrs({ size: 30 })`
            margin-right: 20px;
        `,
        ItemHeader: styled.div`
            font-weight: 500;
            font-size: 12px;
            line-height: 16px;
            text-transform: uppercase;
            color: ${$gray_600};
        `,
        ItemContent: styled.div`
            margin-top: 4px;
            font-size: 14px;
            line-height: 16px;
        `,
        ItemAction: styled.div`
            font-size: 14px;
            line-height: 16px;
            color: #3068f4;
            float: right;
            cursor: pointer;
            position: absolute;
            top: 17px;
            right: 15px;
        `,
    },
    SaveButton: styled(Button)``,
    LanguageSelect: styled(SelectField)`
        margin: 0;
        padding: 0;
        & > label {
            display: none;
        }
        & .select-field {
            width: 80px;
        }
        & .react-select__control {
            border: none;
            min-height: 16px;
            height: 16px;
            & * {
                padding: 0;
                margin: 0;
            }
            & .react-select__indicator-separator {
                display: none;
            }
            &--is-focused {
                box-shadow: none !important;
            }
        }
    `
}

const languageOptions: { label: OrganizationLanguage; value: OrganizationLanguage }[] = [
    { label: OrganizationLanguage.English, value: OrganizationLanguage.English },
    { label: OrganizationLanguage.Dutch, value: OrganizationLanguage.Dutch },
    { label: OrganizationLanguage.German, value: OrganizationLanguage.German },
]

interface Props {
    showTab: (tab: string) => void
    organization: OrganizationFragment
}

const OrganizationForm =({ organization, showTab }: Props) => (
    <>
        <S.Overview.Container>
            <OverviewItem icon="organisation" title={_('organization.form.organization_name')}>
                {organization.name}
            </OverviewItem>
            <OverviewItem icon="web" title={_('organization.form.sub_domain')}>
                {`https://${organization.subdomain}.evd.events/`}
            </OverviewItem>
            <OverviewItem
                icon="organizationAdmin"
                title={_('organization.form.organization_admin')}
                renderAction={() => (
                    <S.Overview.ItemAction onClick={() => showTab('users')}>
                        {_('organization.form.show_all_admins')}
                    </S.Overview.ItemAction>
                )}
            >
                {`${organization.admins[0].firstName} ${organization.admins[0].lastName}`}
            </OverviewItem>
            <OverviewItem icon="group" title={_('organization.form.meeting_select_client_number')}>
                {organization.externalId}
            </OverviewItem>
            <OverviewItem icon="membership" title={_('organization.form.membership_number')}>
                {organization.id}
            </OverviewItem>
            <OverviewItem icon="calendarToday" title={_('organization.membership.membership_since')}>
                {organization.membershipActivatedAt
                    ? format(toDate(organization.membershipActivatedAt), 'dd/MM/yyyy')
                    : _('organization.membership.not_yet_activated')}
            </OverviewItem>
            <OverviewItem icon="translate" title={_('organization.form.language')}>
                <S.LanguageSelect
                    name="language"
                    label={_('organization.form.language')}
                    options={languageOptions}
                    isClearable={false}
                />
            </OverviewItem>
            <OverviewItem icon="renew" title={_('organization.membership.auto_renewal_date')}>
                {organization.membershipActivatedAt
                    ? format(toDate(organization.membershipExpiresAt), 'dd/MM/yyyy')
                    : _('organization.membership.not_yet_activated')}
            </OverviewItem>
        </S.Overview.Container>

        <Col>
            <S.SectionHeader>{_('organization.form.address_information')}</S.SectionHeader>
            <Row>
                <TextField
                    name="physicalAddress.addressLine1"
                    label={_('organization.form.address_line_1')}
                    lg={1 / 2}
                    isRequired
                />
                <TextField
                    name="physicalAddress.addressLine2"
                    label={_('organization.form.address_line_2')}
                    lg={1 / 2}
                    isRequired
                />
                <TextField
                    name="physicalAddress.postalCode"
                    label={_('organization.form.postal_code')}
                    lg={1 / 2}
                    isRequired
                />
                <TextField
                    name="physicalAddress.city"
                    label={_('organization.form.city')}
                    lg={1 / 2}
                    isRequired
                />
                <TextField name="physicalAddress.state" label={_('organization.form.state')} lg={1 / 2} />
                <TextField
                    name="physicalAddress.country"
                    label={_('organization.form.country')}
                    lg={1 / 2}
                    isRequired
                />
            </Row>

            <S.SectionHeader>{_('organization.form.billing_information')}</S.SectionHeader>
            <Row>
                <TextField
                    name="billingAddress.addressLine1"
                    label={_('organization.form.address_line_1')}
                    lg={1 / 2}
                    isRequired
                />
                <TextField
                    name="billingAddress.addressLine2"
                    label={_('organization.form.address_line_2')}
                    lg={1 / 2}
                    isRequired
                />
                <TextField
                    name="billingAddress.postalCode"
                    label={_('organization.form.postal_code')}
                    lg={1 / 2}
                    isRequired
                />
                <TextField
                    name="billingAddress.city"
                    label={_('organization.form.city')}
                    lg={1 / 2}
                    isRequired
                />
                <TextField name="billingAddress.state" label={_('organization.form.state')} lg={1 / 2} />
                <TextField
                    name="billingAddress.country"
                    label={_('organization.form.country')}
                    lg={1 / 2}
                    isRequired
                />
                <TextField
                    name="cocNumber"
                    label={_('organization.form.cocNumber')}
                    lg={1 / 2}
                    isRequired
                />
                <TextField
                    name="vatNumber"
                    label={_('organization.form.vatNumber')}
                    lg={1 / 2}
                    isRequired
                />
                <TextField
                    name="billingDepartment"
                    label={_('organization.form.billingDepartment')}
                    lg={1 / 2}
                />
            </Row>

            <S.SectionHeader>{_('organization.form.legal_representative')}</S.SectionHeader>
            <Row>
                <TextField
                    name="contactName"
                    label={_('organization.form.contact_name')}
                    lg={1 / 2}
                    isRequired
                />
                <TextField
                    name="contactPosition"
                    label={_('organization.form.contact_position')}
                    lg={1 / 2}
                    isRequired
                />
                <TextField
                    name="contactEmail"
                    label={_('organization.form.contact_email')}
                    lg={1 / 2}
                    isRequired
                />
                <TextField
                    name="contactPhone"
                    label={_('organization.form.contact_phone')}
                    lg={1 / 2}
                    isRequired
                />
            </Row>
        </Col>
    </>
)

export default OrganizationForm
